import React, { useState, useEffect, useCallback, useRef } from "react";
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import KanbanLane from "./KanbanLane";
import KanbanCard from "./KanbanCard";
import SlideOverPanel from "../Requests/SlideOverPanel";
import { fetchKanbanBoard, kanbanMoveCard, approveKanbanCard, holdKanbanCard, fetchRejectionTargets, rejectRequestWithTarget } from "../../../api/t2tRequestsApi";
import { getData } from "../../../FetchApiServices";
import { buildChatActivityStreamUrl } from "../../../api/t2tMessagesApi";

const SSE_URL = "/api/t2t/requests/sse";

const GROUP_MODES = [
  { value: "stage",    label: "By Stage",    icon: "bi-diagram-3"    },
  { value: "status",   label: "By Status",   icon: "bi-circle-half"  },
  { value: "assignee", label: "By Assignee", icon: "bi-people"       },
];

export default function KanbanBoard() {
  const [workflows,      setWorkflows]      = useState([]);
  const [selectedWfId,   setSelectedWfId]   = useState("");
  const [groupBy,        setGroupBy]        = useState("stage");
  const [boardData,      setBoardData]      = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState(null);
  const [collapsedLanes, setCollapsedLanes] = useState(new Set());
  const [activeCard,     setActiveCard]     = useState(null);
  const [confirmMove,    setConfirmMove]    = useState(null);
  const [moving,         setMoving]         = useState(false);
  const [actionModal,    setActionModal]    = useState(null);
  const [actionComment,  setActionComment]  = useState("");
  const [actionResume,   setActionResume]   = useState("");
  const [actioning,      setActioning]      = useState(false);
  // BUG-62: return-stage picker for reject (mirrors detail-page UX)
  const [rejTargets,     setRejTargets]     = useState([]);
  const [rejTargetId,    setRejTargetId]    = useState("");
  const [rejLoading,     setRejLoading]     = useState(false);
  const [slideOverId,    setSlideOverId]    = useState(null);
  // T2T-05-C4: per-request unread message count, updated live via chat-activity SSE.
  const [chatUnread,     setChatUnread]     = useState({}); // { [request_id]: count }

  const sseRef     = useRef(null);
  const chatSseRef = useRef(null);
  // BUG-63: coalesce concurrent silent reloads (SSE + post-action) so we don't double-fetch
  const reloadInFlightRef = useRef(false);

  // ── Workflow list ────────────────────────────────────────────────
  useEffect(() => {
    getData("t2t/workflows?page=1&limit=100&active=true").then(r => {
      if (!r.error) {
        const raw = r.data?.data;
        const wfs =
          Array.isArray(raw?.data)      ? raw.data      :
          Array.isArray(raw?.rows)      ? raw.rows      :
          Array.isArray(raw?.workflows) ? raw.workflows :
          Array.isArray(raw)            ? raw           :
          Array.isArray(r.data)         ? r.data        : [];
        setWorkflows(wfs);
        if (wfs.length > 0) setSelectedWfId(String(wfs[0].workflow_id));
      }
    });
  }, []);

  // ── Load board ───────────────────────────────────────────────────
  // BUG-63: opts.silent skips setLoading(true) so the existing board stays on
  // screen while we fetch — used for post-action reloads, optimistic-move
  // reconcile, and SSE board_update events. Manual Refresh stays loud.
  const loadBoard = useCallback(async (wfId, mode, opts = {}) => {
    if (!wfId) return;
    if (opts.silent && reloadInFlightRef.current) return; // coalesce
    reloadInFlightRef.current = true;
    if (!opts.silent) {
      setLoading(true);
      setError(null);
    }
    const r = await fetchKanbanBoard(wfId, mode || groupBy);
    if (r.error) {
      if (!opts.silent) setError(r.error?.message || "Failed to load board.");
    } else {
      const newData = r.data?.data;
      if (newData) {
        setBoardData(newData);
      } else if (!opts.silent) {
        // Explicit load with no data → honest empty state
        setBoardData(null);
      }
      // Silent reconcile returning null → keep existing board (defensive)
    }
    if (!opts.silent) setLoading(false);
    reloadInFlightRef.current = false;
  }, [groupBy]);

  useEffect(() => {
    if (selectedWfId) loadBoard(selectedWfId, groupBy);
    setCollapsedLanes(new Set());
  }, [selectedWfId, groupBy, loadBoard]);

  // ── SSE ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedWfId) return;
    const token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken") || "";
    const es = new EventSource(`${SSE_URL}?token=${encodeURIComponent(token)}`);
    sseRef.current = es;
    es.onmessage = e => {
      // BUG-63: SSE-driven reload stays silent so live updates don't blank the board
      try { if (JSON.parse(e.data).type === "board_update") loadBoard(selectedWfId, groupBy, { silent: true }); }
      catch (_) {}
    };
    es.onerror = () => es.close();
    return () => es.close();
  }, [selectedWfId, groupBy, loadBoard]);

  // ── T2T-05-C4: chat-activity SSE — increment unread badge on incoming chat
  // events for any visible card (skip events the current user sent themselves).
  useEffect(() => {
    const url = buildChatActivityStreamUrl();
    const es = new EventSource(url);
    chatSseRef.current = es;
    const handler = (e) => {
      try {
        const evt = JSON.parse(e.data || "{}");
        if (!evt?.request_id) return;
        const meRaw = (() => { try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; } })();
        const myId = meRaw?.user_id ?? meRaw?.users?.[0]?.user_id;
        if (myId && Number(evt.sender_id) === Number(myId)) return;
        // Skip if the slide-over for this request is open (they're watching it).
        if (slideOverId && Number(slideOverId) === Number(evt.request_id)) return;
        setChatUnread((prev) => ({
          ...prev,
          [evt.request_id]: (prev[evt.request_id] || 0) + 1,
        }));
      } catch (_) { /* swallow */ }
    };
    es.addEventListener("request.chat_activity", handler);
    es.onerror = () => { try { es.close(); } catch (_) { /* gone */ } };
    return () => { try { es.close(); } catch (_) { /* gone */ } };
  }, [slideOverId]);

  // Clear unread count when user opens a card via slide-over
  useEffect(() => {
    if (slideOverId) {
      setChatUnread((prev) => {
        if (!prev[slideOverId]) return prev;
        const next = { ...prev };
        delete next[slideOverId];
        return next;
      });
    }
  }, [slideOverId]);

  // ── dnd-kit ──────────────────────────────────────────────────────
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const handleDragStart = ({ active }) => {
    const { card } = active.data.current || {};
    if (card) setActiveCard(card);
  };

  const handleDragEnd = ({ active, over }) => {
    setActiveCard(null);
    if (!over || !active || groupBy !== "stage") return;
    const { card, laneId } = active.data.current || {};
    const toLaneId = over.id;
    if (!card || String(laneId) === String(toLaneId)) return;
    const toLane = (boardData?.lanes || []).find(
      l => String(l.lane_id ?? l.stage_id) === String(toLaneId) && l.stage_id
    );
    if (!toLane) return;
    // BUG-63: preserve fromLaneId so we can revert an optimistic move on API failure
    setConfirmMove({ card, toLane, fromLaneId: laneId });
  };

  // BUG-63: optimistic move — snap card into destination lane immediately,
  // then await the API. On failure revert to the snapshot; on success silent-
  // reconcile to pick up server-side side effects (stage transitions, system
  // actions, etc.) without blanking the board.
  const handleConfirmMove = async () => {
    if (!confirmMove) return;
    const { card, toLane, fromLaneId } = confirmMove;
    const snapshot = boardData;
    const toLaneKey = String(toLane.lane_id ?? toLane.stage_id);
    const fromKey = String(fromLaneId);

    const optimistic = boardData ? {
      ...boardData,
      lanes: (boardData.lanes || []).map((lane) => {
        const lk = String(lane.lane_id ?? lane.stage_id);
        if (lk === fromKey) {
          return { ...lane, cards: (lane.cards || []).filter((c) => c.request_id !== card.request_id) };
        }
        if (lk === toLaneKey) {
          return { ...lane, cards: [...(lane.cards || []), card] };
        }
        return lane;
      }),
    } : boardData;

    setBoardData(optimistic);
    setConfirmMove(null);
    setMoving(true);
    const r = await kanbanMoveCard(card.request_id, { to_stage_id: toLane.stage_id });
    setMoving(false);
    if (r.error) {
      setBoardData(snapshot);
      alert(r.error?.message || "Move failed.");
    } else {
      loadBoard(selectedWfId, groupBy, { silent: true });
    }
  };

  // ── Card actions ─────────────────────────────────────────────────
  const handleCardAction = (requestId, action) => {
    if (action === "approve") {
      setActioning(true);
      approveKanbanCard(requestId).then(r => {
        setActioning(false);
        if (r.error) alert(r.error?.message || "Action failed.");
        else loadBoard(selectedWfId, groupBy, { silent: true });
      });
    } else {
      setActionComment("");
      setActionResume("");
      setRejTargets([]);
      setRejTargetId("");
      setActionModal({ requestId, action });
      // BUG-62: prefetch allowed return stages for the reject picker
      if (action === "reject") {
        setRejLoading(true);
        fetchRejectionTargets(requestId).then(resp => {
          if (!resp.error) {
            const raw = resp.data?.data;
            setRejTargets(Array.isArray(raw) ? raw : []);
          }
          setRejLoading(false);
        });
      }
    }
  };

  const handleActionSubmit = async () => {
    if (!actionModal || actionComment.trim().length < 5) return;
    const { requestId, action } = actionModal;
    // BUG-62: reject now requires an explicit target stage
    if (action === "reject" && !rejTargetId) return;
    setActioning(true);
    const r = action === "reject"
      ? await rejectRequestWithTarget(requestId, { return_to_stage_id: parseInt(rejTargetId, 10), comment: actionComment })
      : await holdKanbanCard(requestId, { comment: actionComment, expected_resume: actionResume || undefined });
    setActioning(false);
    setActionModal(null);
    if (r.error) alert(r.error?.message || "Action failed.");
    else loadBoard(selectedWfId, groupBy, { silent: true });
  };

  const toggleLane = (lk) => {
    setCollapsedLanes(prev => {
      const next = new Set(prev);
      next.has(lk) ? next.delete(lk) : next.add(lk);
      return next;
    });
  };

  const totalCards = (boardData?.lanes || []).reduce((s, l) => s + (l.cards || []).length, 0);

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>

      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <div className="kb-toolbar">
        <div className="kb-toolbar-title">
          <i className="bi bi-kanban-fill" style={{ fontSize: 14 }} />
          Kanban Board
        </div>

        <select
          className="kb-workflow-select"
          value={selectedWfId}
          onChange={e => setSelectedWfId(e.target.value)}
          title="Select workflow"
        >
          {workflows.map(wf => (
            <option key={wf.workflow_id} value={String(wf.workflow_id)}>
              {wf.name}
            </option>
          ))}
        </select>

        {/* Group-by pill tabs */}
        <div className="kb-mode-tabs" role="group" aria-label="Group by">
          {GROUP_MODES.map(m => (
            <button
              key={m.value}
              type="button"
              className={`kb-mode-tab${groupBy === m.value ? " active" : ""}`}
              onClick={() => setGroupBy(m.value)}
              title={m.label}
            >
              <i className={`bi ${m.icon}`} style={{ fontSize: 12 }} />
              {m.label}
            </button>
          ))}
        </div>

        {boardData && (
          <span className="kb-flight-badge">
            <i className="bi bi-layers me-1" style={{ fontSize: 10 }} />
            {totalCards} in flight
          </span>
        )}

        <button
          className="kb-refresh-btn"
          onClick={() => loadBoard(selectedWfId, groupBy)}
          title="Refresh board"
          aria-label="Refresh board"
        >
          <i className={`bi bi-arrow-clockwise${loading ? " t2t-spin" : ""}`} />
        </button>
      </div>

      {/* ── Board area ──────────────────────────────────────────── */}
      <div className="kb-board-area">

        {/* Skeleton */}
        {loading && (
          <div className="kb-board-scroll">
            {[1, 2, 3].map(n => (
              <div key={n} className="kb-skeleton-lane">
                <div className="kb-skeleton-header">
                  <span className="kb-skeleton-bar" style={{ display: "block", width: "60%", height: 14 }} />
                </div>
                <div className="kb-skeleton-body">
                  {[1, 2, 3].map(c => (
                    <div key={c} className="kb-skeleton-card">
                      <span className="kb-skeleton-bar" style={{ display: "block", width: "35%", height: 10, marginBottom: 8 }} />
                      <span className="kb-skeleton-bar" style={{ display: "block", width: "85%", height: 13, marginBottom: 8 }} />
                      <span className="kb-skeleton-bar" style={{ display: "block", width: "50%", height: 10 }} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="kb-board-empty">
            <i className="bi bi-exclamation-circle kb-board-empty-icon" style={{ color: "var(--t2t-danger)" }} />
            <div className="kb-board-empty-title" style={{ color: "var(--t2t-danger)" }}>Failed to load board</div>
            <div className="kb-board-empty-sub">{error}</div>
            <button
              className="btn btn-sm btn-outline-secondary mt-2"
              style={{ fontSize: 12 }}
              onClick={() => loadBoard(selectedWfId, groupBy)}
            >
              <i className="bi bi-arrow-clockwise me-1" />Retry
            </button>
          </div>
        )}

        {/* Empty — no workflow selected */}
        {!loading && !error && !boardData && selectedWfId && (
          <div className="kb-board-empty">
            <i className="bi bi-kanban kb-board-empty-icon" />
            <div className="kb-board-empty-title">No data for this workflow</div>
            <div className="kb-board-empty-sub">Submit a request to see it here.</div>
          </div>
        )}

        {/* Board */}
        {!loading && !error && boardData && (
          <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div className="kb-board-scroll t2t-kanban-scroll">
              {(boardData.lanes || []).map(lane => {
                const lk = lane.lane_id ?? lane.stage_id;
                return (
                  <KanbanLane
                    key={String(lk)}
                    lane={lane}
                    collapsed={collapsedLanes.has(lk)}
                    onToggleCollapse={() => toggleLane(lk)}
                    onAction={handleCardAction}
                    onCardClick={(requestId) => setSlideOverId(requestId)}
                    chatUnread={chatUnread}
                  />
                );
              })}
            </div>

            <DragOverlay>
              {activeCard ? (
                <div style={{ transform: "rotate(2deg)", opacity: 0.9, width: 272 }}>
                  <KanbanCard card={activeCard} laneId={null} />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* ── Reject / Hold modal ──────────────────────────────────── */}
      {actionModal && (
        <div
          className="kb-modal-overlay"
          onClick={() => setActionModal(null)}
        >
          <div
            className="kb-modal"
            onClick={e => e.stopPropagation()}
          >
            <div className="kb-modal-header">
              <div className="kb-modal-title">
                {actionModal.action === "reject" ? (
                  <i className="bi bi-x-circle-fill" style={{ color: "var(--t2t-danger)", fontSize: 16 }} />
                ) : (
                  <i className="bi bi-pause-circle-fill" style={{ color: "var(--t2t-warning)", fontSize: 16 }} />
                )}
                {actionModal.action === "reject" ? "Reject Request" : "Put on Hold"}
              </div>
              <button
                className="btn-close"
                style={{ fontSize: 11 }}
                onClick={() => setActionModal(null)}
                aria-label="Close"
              />
            </div>

            <div className="kb-modal-body">
              <p style={{ fontSize: 13, color: "var(--t2t-text-secondary)", marginBottom: 14 }}>
                {actionModal.action === "reject"
                  ? "This request will be returned to a previous stage for correction. Pick the stage below."
                  : "This request will be paused and marked as on hold until resumed."}
              </p>

              {/* BUG-62: return-stage picker for reject (parity with detail page) */}
              {actionModal.action === "reject" && (
                <div style={{ marginBottom: 12 }}>
                  <label>Return to Stage <span style={{ color: "var(--t2t-danger)" }}>*</span></label>
                  {rejLoading ? (
                    <div style={{ fontSize: 12, color: "var(--t2t-text-muted)", padding: "8px 0" }}>Loading stages…</div>
                  ) : rejTargets.length > 0 ? (
                    <>
                      {rejTargets.some((t) => t.is_previous_stage) && (
                        <button
                          type="button"
                          className={`btn btn-sm w-100 mb-2 ${rejTargetId === String(rejTargets.find((t) => t.is_previous_stage)?.id) ? "btn-primary" : "btn-outline-primary"}`}
                          style={{ fontSize: 13, textAlign: "left" }}
                          onClick={() => setRejTargetId(String(rejTargets.find((t) => t.is_previous_stage)?.id || ""))}
                        >
                          ↩ Return to Previous Stage
                        </button>
                      )}
                      <select
                        className="form-control form-control-sm"
                        style={{ fontSize: 13 }}
                        value={rejTargetId}
                        onChange={(e) => setRejTargetId(e.target.value)}
                      >
                        <option value="">Choose a stage to return to…</option>
                        {rejTargets.map((t) => (
                          <option key={t.id} value={String(t.id)}>{t.label || t.name}</option>
                        ))}
                      </select>
                    </>
                  ) : (
                    <div style={{ fontSize: 12, color: "var(--t2t-text-muted)", padding: "8px 0" }}>
                      No return stages available.
                    </div>
                  )}
                </div>
              )}

              <label>Comment <span style={{ color: "var(--t2t-danger)" }}>*</span></label>
              <textarea
                className="form-control form-control-sm"
                rows={3}
                style={{ fontSize: 13, resize: "none" }}
                placeholder="Reason (minimum 5 characters)…"
                value={actionComment}
                onChange={e => setActionComment(e.target.value)}
                autoFocus
              />

              {actionModal.action === "hold" && (
                <>
                  <label style={{ marginTop: 12 }}>Expected Resume Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    style={{ fontSize: 13 }}
                    value={actionResume}
                    onChange={e => setActionResume(e.target.value)}
                  />
                </>
              )}
            </div>

            <div className="kb-modal-footer">
              <button
                className="btn btn-sm btn-outline-secondary"
                style={{ fontSize: 13 }}
                onClick={() => setActionModal(null)}
                disabled={actioning}
              >
                Cancel
              </button>
              <button
                className="btn btn-sm"
                style={{
                  fontSize: 13,
                  background: actionModal.action === "reject" ? "var(--t2t-danger)" : "var(--t2t-warning)",
                  color: "#fff",
                  border: "none",
                  opacity: (actioning || actionComment.trim().length < 5 || (actionModal.action === "reject" && !rejTargetId)) ? 0.4 : 1,
                  cursor:  (actioning || actionComment.trim().length < 5 || (actionModal.action === "reject" && !rejTargetId)) ? "not-allowed" : "pointer",
                }}
                onClick={handleActionSubmit}
                disabled={actioning || actionComment.trim().length < 5 || (actionModal.action === "reject" && !rejTargetId)}
              >
                {actioning ? (
                  <><span className="spinner-border spinner-border-sm me-1" />Processing…</>
                ) : actionModal.action === "reject" ? "Confirm Rejection" : "Confirm Hold"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm drag-move modal ──────────────────────────────── */}
      {confirmMove && (
        <div
          className="kb-modal-overlay"
          onClick={() => setConfirmMove(null)}
        >
          <div
            className="kb-modal"
            onClick={e => e.stopPropagation()}
          >
            <div className="kb-modal-header">
              <div className="kb-modal-title">
                <i className="bi bi-arrows-move" style={{ color: "var(--t2t-primary)", fontSize: 15 }} />
                Move Request
              </div>
              <button
                className="btn-close"
                style={{ fontSize: 11 }}
                onClick={() => setConfirmMove(null)}
                aria-label="Close"
              />
            </div>

            <div className="kb-modal-body">
              <p style={{ fontSize: 13, color: "var(--t2t-text-secondary)", marginBottom: 6 }}>
                Move{" "}
                <span className="font-monospace fw-semibold" style={{ color: "var(--t2t-text-primary)" }}>
                  {confirmMove.card.ref_id}
                </span>{" "}
                to stage{" "}
                <strong style={{ color: "var(--t2t-text-primary)" }}>{confirmMove.toLane.name}</strong>?
              </p>
              <p style={{ fontSize: 12, color: "var(--t2t-text-muted)", marginBottom: 0 }}>
                This will trigger the approval action for this stage.
              </p>
            </div>

            <div className="kb-modal-footer">
              <button
                className="btn btn-sm btn-outline-secondary"
                style={{ fontSize: 13 }}
                onClick={() => setConfirmMove(null)}
                disabled={moving}
              >
                Cancel
              </button>
              <button
                className="btn btn-sm"
                style={{ fontSize: 13, background: "var(--t2t-primary)", color: "#fff", border: "none" }}
                onClick={handleConfirmMove}
                disabled={moving}
              >
                {moving ? (
                  <><span className="spinner-border spinner-border-sm me-1" />Moving…</>
                ) : (
                  <><i className="bi bi-check2 me-1" />Confirm Move</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Slide-over detail ────────────────────────────────────── */}
      {slideOverId && (
        <SlideOverPanel
          requestId={slideOverId}
          onClose={() => setSlideOverId(null)}
        />
      )}
    </div>
  );
}
